import React from 'react';

import {constraintSpecToClientTagSchemaConstraint} from 'antibodies/schema-configuration/AntibodyComponentSchemaConfiguration';
import {AntibodyEnforcedConstraintSpec} from 'antibodies/schema-configuration/AntibodyEnforcedDataModelConstants';
import {ClientTagSchemaConstraint, TagSchemaConstraint} from 'constants/RegistryConstants';
import {TypeSpecificConstraintType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ClientSchemaField} from 'registry/field/FieldTable';
import ConstraintTable, {ConstraintTableType} from 'settings/registry/ConstraintTable';
import getTestProps from 'test/lib/cypress/getTestProps';

import MigrationPreviewSection from './MigrationPreviewSection';

export interface ConstraintMigrationPreviewProps {
  existingConstraint: ClientTagSchemaConstraint | TagSchemaConstraint | null;
  enforcedConstraintSpec: AntibodyEnforcedConstraintSpec;
  enforcedFields: Array<ClientSchemaField>;
  folderItemType: string | null;
}

function typeSpecificConstraintSatisfiesSpec(
  constraint: ClientTagSchemaConstraint | TagSchemaConstraint,
  enforcedTypeSpecificConstraint: NonNullable<
    AntibodyEnforcedConstraintSpec['enforcedTypeSpecificConstraint']
  >
): boolean {
  const {constraintType: enforcedConstraintType} = enforcedTypeSpecificConstraint;
  const {typeSpecificConstraintConfig} = constraint;
  if (typeSpecificConstraintConfig == null) {
    if (enforcedConstraintType === TypeSpecificConstraintType.UNIQUE_RESIDUES) {
      return (
        constraint.uniqueResidues &&
        constraint.areUniqueResiduesCaseSensitive === enforcedTypeSpecificConstraint.isCaseSensitive
      );
    }
    if (enforcedConstraintType === TypeSpecificConstraintType.UNIQUE_CANONICAL_SMILES) {
      return constraint.uniqueCanonicalSmiles;
    }
    return false;
  }
  return (
    typeSpecificConstraintConfig.constraintType === enforcedConstraintType &&
    (enforcedConstraintType !== TypeSpecificConstraintType.UNIQUE_RESIDUES ||
      constraint.areUniqueResiduesCaseSensitive === enforcedTypeSpecificConstraint.isCaseSensitive)
  );
}

function constraintSatisfiesSpec(
  constraint: ClientTagSchemaConstraint | TagSchemaConstraint,
  enforcedConstraintSpec: AntibodyEnforcedConstraintSpec
): boolean {
  const {enforcedTypeSpecificConstraint, enforcedMinimumFieldDefinitionNames} = enforcedConstraintSpec;
  return (
    // Assume that enforced fields are not yet present on the schema, so the constraint must be replaced if the spec includes any fields.
    enforcedMinimumFieldDefinitionNames.length === 0 &&
    (enforcedTypeSpecificConstraint == null ||
      typeSpecificConstraintSatisfiesSpec(constraint, enforcedTypeSpecificConstraint))
  );
}

function getConstraintChangeAndText(
  enforcedConstraintSpec: AntibodyEnforcedConstraintSpec,
  existingConstraint: ClientTagSchemaConstraint | TagSchemaConstraint | null,
  enforcedFields: Array<ClientSchemaField>
): {newConstraint: ClientTagSchemaConstraint | TagSchemaConstraint | null; text: string} {
  if (existingConstraint == null) {
    return {
      newConstraint: constraintSpecToClientTagSchemaConstraint(enforcedConstraintSpec, enforcedFields),
      text: 'New constraint will be added to the schema.',
    };
  }

  if (constraintSatisfiesSpec(existingConstraint, enforcedConstraintSpec)) {
    return {
      newConstraint: null,
      text: 'No changes to constraint.',
    };
  }

  return {
    newConstraint: constraintSpecToClientTagSchemaConstraint(enforcedConstraintSpec, enforcedFields),
    text: 'Existing constraint will be replaced with new constraint.',
  };
}

export default function ConstraintMigrationPreview({
  existingConstraint,
  enforcedConstraintSpec,
  enforcedFields,
  folderItemType,
}: ConstraintMigrationPreviewProps): JSX.Element {
  const {newConstraint, text} = getConstraintChangeAndText(
    enforcedConstraintSpec,
    existingConstraint,
    enforcedFields
  );
  return (
    <div {...getTestProps({component: 'ConstraintMigrationPreview', element: 'root'})}>
      <MigrationPreviewSection title="Constraint" description={text}>
        {newConstraint != null && (
          <ConstraintTable
            constraint={newConstraint}
            folderItemType={folderItemType}
            onAddClick={() => {}}
            onRemoveClick={() => {}}
            writable={false}
            tableType={ConstraintTableType.SCHEMA}
          />
        )}
      </MigrationPreviewSection>
    </div>
  );
}
